import React, { useState } from 'react';
import { css } from 'emotion';
import styled from '@emotion/styled';
import { useForm } from 'react-hook-form';
import Alert from 'react-bootstrap/lib/Alert';
import { Button } from 'shared/Button/NewButton';
import { isValidEmail } from 'client/user';
import { useT } from 'client/i18n/hooks/useT';
import { INTEGRATION_CONNECTION_MENU_MODE } from 'client/screens/IntegrationConnectionScreen/constants';
import { FieldInput } from '../FieldInput';
import { ButtonWrapper, TextButton, FieldWrapper } from '../Login/styles';

const Text = styled.p`
  margin-bottom: 32px;
  text-align: center;
`;

const LoginFooter = styled.p`
  text-align: center;
`;

type RequestResetPasswordProps = {
  clickLogIn: () => void;
  onSubmit: (data: RequestResetPasswordData) => Promise<void>;
  onVideoLanderSubmit?: (data: RequestResetPasswordData) => Promise<void>;
  setIntegrationConnectionScreenStatus?: (
    value: INTEGRATION_CONNECTION_MENU_MODE
  ) => void;
};

type RequestResetPasswordData = { email: string };

export const RequestResetPassword = ({
  clickLogIn,
  onSubmit,
  onVideoLanderSubmit,
  setIntegrationConnectionScreenStatus,
}: RequestResetPasswordProps) => {
  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
  } = useForm();
  const t = useT();
  const [formError, setFormError] = useState<null | string>(null);

  const onValid = (data: RequestResetPasswordData) => {
    if (onVideoLanderSubmit) {
      onVideoLanderSubmit(data).catch(({ message }) => setFormError(message));
    } else {
      onSubmit(data).catch(({ message }) => setFormError(message));
    }
  };

  const handleRedirectToLogin = () => {
    if (setIntegrationConnectionScreenStatus) {
      setIntegrationConnectionScreenStatus('login');
    } else {
      clickLogIn();
    }
  };

  return (
    <div>
      <Text>
        {t(
          'RequestResetPassword.Instructions',
          "Type your email below, and we'll email you a link to reset your password.",
          'Instructions on how to reset user password'
        )}
      </Text>
      <form
        onSubmit={handleSubmit(onValid)}
        className={css`
          text-align: center;
        `}
        noValidate={true}
      >
        {formError && <Alert bsStyle="danger">{formError}</Alert>}
        <FieldWrapper>
          <FieldInput
            {...register('email', {
              required: t(
                'RequestResetPassword.EmailFieldRequired',
                'Email is required',
                'Email field required message'
              ),
              validate: email =>
                isValidEmail(email) ||
                t(
                  'RequestResetPassword.EmailFieldInvalid',
                  'Must provide a valid email address',
                  'Email field invalid message'
                ),
            })}
            name="email"
            placeholder={t(
              'RequestResetPassword.EmailFieldPlaceholder',
              'Email',
              'Email field placeholder'
            )}
            type="email"
            error={errors.email}
          />
        </FieldWrapper>
        <ButtonWrapper>
          <Button
            color="purple"
            type="submit"
            isDisabled={isSubmitting}
            className={css`
              width: 200px;
            `}
          >
            {t(
              'RequestResetPassword.ResetPasswordButtonLabel',
              'Reset Password',
              'Reset password button text'
            )}
          </Button>
        </ButtonWrapper>
      </form>
      <LoginFooter>
        <TextButton onClick={handleRedirectToLogin}>
          {t('Button.Login', 'Log in', 'Log in button text')}
        </TextButton>
      </LoginFooter>
    </div>
  );
};
